import { createAsyncThunk } from '@reduxjs/toolkit'
import { REQUEST_OPTIONS } from '../../../constants/request-options'
import { AxiosService } from '../../../services/api'
import { createThunkName } from '../../../utils/createThunkname'
import {
  AddCustomerDto,
  CancelInsuredDto,
  ExportCustomerQueryParams,
  ExportTransactionByCustomerIdQueryParams,
  GetAllCustomersQueryParams,
  GetAllCustomersResponse,
  GetAllTransactionByCustomerIdQueryParams,
  GetAllTransactionByCustomerIdResponse,
  GetCountTransactionByCustomerIdQueryParams,
  GetCountTransactionByCustomerIdResponse,
  GetCustomerContactResponse,
  GetCustomerInfoResponse,
  GetCustomerNameResponse,
  GetCustomerNoteResponse,
  GetCustomerStatusResponse,
  GetCustomerTimestampResponse,
  GetCustomerTaxInvoiceByTransactionIdResponse,
  GetTransactionDetailResponse,
  GetTransactionInsuredResponse,
  GetTransactionSummaryResponse,
  UpdateCustomerContactDto,
  UpdateCustomerInfoDto,
  UpdateCustomerNoteDto,
  UpdateCustomerSentTaxInvoiceDto,
  UpdateCustomerStatusDto,
  UpdateCustomerTransactionTaxInvoiceDto,
  UpdateTransactionInsuredDto
} from './types'
import { CUSTOMER_URLS } from '../../../configs/api'
import { saveData } from '../../../utils/downloadFile'

const PREFIX = 'Customer'

export const CustomerService = {
  getAllCustomersAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getAllCustomers'),
    async (queryParams: GetAllCustomersQueryParams) => {
      try {
        const result = await AxiosService.get<GetAllCustomersResponse>(CUSTOMER_URLS.getAllCustomers, {
          params: queryParams
        })
        return result.data
      } catch (error) {
        throw error
      }
    }
  ),
  exportCustomersAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'exportCustomers'),
    async (queryParams: ExportCustomerQueryParams) => {
      try {
        const result = await AxiosService.get(CUSTOMER_URLS.exportCustomers, {
          params: queryParams,
          ...REQUEST_OPTIONS
        })
        saveData(result, result.request.getResponseHeader('Content-Disposition'))
      } catch (error) {
        throw error
      }
    }
  ),
  addCustomerAsyncThunk: createAsyncThunk(createThunkName(PREFIX, 'addCustomer'), async (dto: AddCustomerDto) => {
    try {
      await AxiosService.post(CUSTOMER_URLS.addCustomer, dto)
    } catch (error) {
      throw error
    }
  }),
  getCustomerNameAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getCustomerName'),
    async (customerId: string) => {
      try {
        const result = await AxiosService.get<GetCustomerNameResponse>(CUSTOMER_URLS.getCustomerName(customerId))
        return result.data.fullName
      } catch (error) {
        throw error
      }
    }
  ),
  getCustomerStatusAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getCustomerStatus'),
    async (customerId: string) => {
      try {
        const result = await AxiosService.get<GetCustomerStatusResponse>(CUSTOMER_URLS.getCustomerStatus(customerId))
        return result.data.isActive
      } catch (error) {
        throw error
      }
    }
  ),
  getCustomerInfoAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getCustomerInfo'),
    async (customerId: string) => {
      try {
        const result = await AxiosService.get<GetCustomerInfoResponse>(CUSTOMER_URLS.getCustomerInfo(customerId))
        return result.data.info
      } catch (error) {
        throw error
      }
    }
  ),
  getCustomerContactAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getCustomerContact'),
    async (customerId: string) => {
      try {
        const result = await AxiosService.get<GetCustomerContactResponse>(CUSTOMER_URLS.getCustomerContact(customerId))
        return result.data.contactInfo
      } catch (error) {
        throw error
      }
    }
  ),
  getCustomerNoteAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getCustomerNote'),
    async (customerId: string) => {
      try {
        const result = await AxiosService.get<GetCustomerNoteResponse>(CUSTOMER_URLS.getCustomerNote(customerId))
        return result.data.note
      } catch (error) {
        throw error
      }
    }
  ),
  getCustomerTimestampAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getCustomerTimestamp'),
    async (customerId: string) => {
      try {
        const result = await AxiosService.get<GetCustomerTimestampResponse>(
          CUSTOMER_URLS.getCustomerTimestamp(customerId)
        )
        return result.data.timestamp
      } catch (error) {
        throw error
      }
    }
  ),
  updateCustomerStatusAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'updateCustomerStatus'),
    async (dto: UpdateCustomerStatusDto) => {
      try {
        await AxiosService.put(CUSTOMER_URLS.updateCustomerStatus, dto)
      } catch (error) {
        throw error
      }
    }
  ),
  updateCustomerInfoAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'updateCustomerInfo'),
    async (dto: UpdateCustomerInfoDto) => {
      try {
        await AxiosService.put(CUSTOMER_URLS.updateCustomerInfo, dto)
      } catch (error) {
        throw error
      }
    }
  ),
  updateCustomerContactAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'updateCustomerContact'),
    async (dto: UpdateCustomerContactDto) => {
      try {
        await AxiosService.put(CUSTOMER_URLS.updateCustomerContact, dto)
      } catch (error) {
        throw error
      }
    }
  ),
  updateCustomerNoteAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'updateCustomerNote'),
    async (dto: UpdateCustomerNoteDto) => {
      try {
        await AxiosService.put(CUSTOMER_URLS.updateCustomerNote, dto)
      } catch (error) {
        throw error
      }
    }
  ),
  getAllTransactionByCustomerIdAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getAllTransactionByCustomerId'),
    async (queryParams: GetAllTransactionByCustomerIdQueryParams) => {
      try {
        const result = await AxiosService.get<GetAllTransactionByCustomerIdResponse>(
          CUSTOMER_URLS.getAllTransactionByCustomerId,
          {
            params: queryParams
          }
        )
        return result.data
      } catch (error) {
        throw error
      }
    }
  ),
  getCountTransactionByCustomerIdAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getCountTransactionByCustomerId'),
    async (queryParams: GetCountTransactionByCustomerIdQueryParams) => {
      try {
        const result = await AxiosService.get<GetCountTransactionByCustomerIdResponse>(
          CUSTOMER_URLS.getCountTransactionByCustomerId,
          { params: queryParams }
        )
        return result.data.customerTransactions
      } catch (error) {
        throw error
      }
    }
  ),
  exportTransactionByCustomerIdAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'exportTransactionByCustomerId'),
    async (queryParams: ExportTransactionByCustomerIdQueryParams) => {
      try {
        const result = await AxiosService.get(CUSTOMER_URLS.exportTransactionByCustomerId, {
          params: queryParams,
          ...REQUEST_OPTIONS
        })
        saveData(result, result.request.getResponseHeader('Content-Disposition'))
      } catch (error) {
        throw error
      }
    }
  ),
  getTransactionInsuredAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getTransactionInsured'),
    async (transactionId: string) => {
      try {
        const result = await AxiosService.get<GetTransactionInsuredResponse>(
          CUSTOMER_URLS.getTransactionInsured(transactionId)
        )
        return result.data.insured
      } catch (error) {
        throw error
      }
    }
  ),
  getTransactionSummaryAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getTransactionSummary'),
    async (transactionId: string) => {
      try {
        const result = await AxiosService.get<GetTransactionSummaryResponse>(
          CUSTOMER_URLS.getTransactionSummary(transactionId)
        )
        return result.data
      } catch (error) {
        throw error
      }
    }
  ),
  getTransactionDetailAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getTransactionDetail'),
    async (transactionId: string) => {
      try {
        const result = await AxiosService.get<GetTransactionDetailResponse>(
          CUSTOMER_URLS.getTransactionDetail(transactionId)
        )
        return result.data
      } catch (error) {
        throw error
      }
    }
  ),
  updateTransactionInsuredAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'updateTransactionInsured'),
    async (dto: UpdateTransactionInsuredDto) => {
      try {
        await AxiosService.put(CUSTOMER_URLS.updateTransactionInsured, dto)
      } catch (error) {
        throw error
      }
    }
  ),
  cancelInsuredAsyncThunk: createAsyncThunk(createThunkName(PREFIX, 'cancelInsured'), async (dto: CancelInsuredDto) => {
    try {
      await AxiosService.put(CUSTOMER_URLS.cancelInsured, dto)
    } catch (error) {
      throw error
    }
  }),
  getCustomerTransactionTaxInvoiceByTransactionIdAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getCustomerTransactionTaxInvoiceByTransactionId'),
    async (transactionId: string) => {
      try {
        const result = await AxiosService.get<GetCustomerTaxInvoiceByTransactionIdResponse>(
          CUSTOMER_URLS.getTransactionTaxInvoiceByTransactionId(transactionId)
        )
        return result.data
      } catch (error) {
        throw error
      }
    }
  ),
  updateCustomerTransactionTaxInvoiceAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'updateCustomerTransactionTaxInvoice'),
    async (dto: UpdateCustomerTransactionTaxInvoiceDto) => {
      try {
        await AxiosService.put(CUSTOMER_URLS.updateTransactionTaxInvoice, dto)
      } catch (error) {
        throw error
      }
    }
  ),
  updateCustomerSentTaxInvoiceAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'updateCustomerSentTaxInvoice'),
    async (dto: UpdateCustomerSentTaxInvoiceDto) => {
      try {
        await AxiosService.put(CUSTOMER_URLS.updateSentTaxInvoice, dto)
      } catch (error) {
        throw error
      }
    }
  )
}
